import {FormPageTypes, FormSectionTypes, FieldOptionType, FormFieldTypes} from 'appRedux/actions/forms/types';
import {WorkflowFieldVariableItemTypes} from 'appRedux/actions/workflow/types';

import {TabItemType} from 'components/TabsWrapper/helper';
import {MdxEditorCustomButtonType} from 'components/MdxEditorField/helper';
import {isImageOptionsList, showOptionsList} from 'components/Forms/FormBuilder/helper';
import {RESOURCE_FIELD_TYPE_CALENDAR} from 'components/Forms/ResourceField/helper';

import {
    OPTIONS_SEPARATOR,
    FORM_FIELD_UPLOADER,
    FORM_FIELD_RESOURCE_FIELD,
    FORM_FIELD_SIGNATURE,
    FORM_FIELD_CHECKBOXES,
    FORM_FIELD_IMAGE_CHECK,
} from 'pages/admin/updateForm/partials/FormStructure/helper';

export const OPTION_ERROR_SEMICOLON = 1;
export const OPTION_ERROR_MIN_TWO_OPTIONS = 2;
export const OPTION_ERROR_MIN_ONE_OPTION = 3;
export const OPTION_ERROR_REPEAT = 4;
export const OPTION_ERROR_HTML_TAG = 5;
export const OPTION_ERROR_MIN_LESS_MAX = 6;
export const OPTION_ERROR_BOTH_RANGES = 7;

export const getOptionsListError = (optionsErrorType: number | null): string => {
    switch (optionsErrorType) {
        case OPTION_ERROR_SEMICOLON:
            return 'orguser.forms.formField.validation.semicolonValidation';
        case OPTION_ERROR_MIN_TWO_OPTIONS:
            return 'orguser.forms.formField.validation.minTwoOptionsValidation';
        case OPTION_ERROR_MIN_ONE_OPTION:
            return 'orguser.forms.formField.validation.minOneOptionValidation';
        case OPTION_ERROR_REPEAT:
            return 'orguser.forms.formField.validation.optionsRepeatValidation';
        case OPTION_ERROR_HTML_TAG:
            return 'orguser.forms.formField.validation.htmlTagsDenied';
        case OPTION_ERROR_MIN_LESS_MAX:
            return 'orguser.forms.formField.validation.minLessThanMax';
        case OPTION_ERROR_BOTH_RANGES:
            return 'orguser.forms.formField.validation.bothRangesRequired';
    }
    return '';
};

export const getOptionsArray = (optionsString: string): string[] => {
    if (!optionsString) {
        return [];
    }
    if (optionsString.includes(OPTIONS_SEPARATOR)) {
        return optionsString.split(OPTIONS_SEPARATOR);
    }
    return [optionsString];
};

export interface ResourceFieldBudgetOptionTypes {
    value: number;
    label: string;
    isHidden?: boolean;
}

export const TYPE_BUDGET_DEFINITION_REQUESTER = 0;
export const TYPE_BUDGET_DEFINITION_AGENT = 1;
export const TYPE_BUDGET_DEFINITION_BOTH = 2;
export const TYPE_BUDGET_DEFINITION_FIXED = 3;

export const getBudgetDefinitionOptions = (isAgentPage: boolean): ResourceFieldBudgetOptionTypes[] => {
    return [
        {
            value: TYPE_BUDGET_DEFINITION_REQUESTER,
            label: 'orguser.forms.formField.typeBudgetDefinitionOptions.requester',
            isHidden: isAgentPage,
        },
        {
            value: TYPE_BUDGET_DEFINITION_AGENT,
            label: 'orguser.forms.formField.typeBudgetDefinitionOptions.agent',
        },
        {
            value: TYPE_BUDGET_DEFINITION_BOTH,
            label: 'orguser.forms.formField.typeBudgetDefinitionOptions.both',
            isHidden: isAgentPage,
        },
        {
            value: TYPE_BUDGET_DEFINITION_FIXED,
            label: 'orguser.forms.formField.typeBudgetDefinitionOptions.fixed',
        },
    ];
};

export const TYPE_BUDGET_INPUT_TEXT = 0;
export const TYPE_BUDGET_INPUT_CALENDAR = 1;

export const budgetInputTextOptions: ResourceFieldBudgetOptionTypes[] = [
    {
        value: TYPE_BUDGET_INPUT_TEXT,
        label: 'orguser.forms.formField.typeBudgetInputOptions.text',
    },
    {
        value: TYPE_BUDGET_INPUT_CALENDAR,
        label: 'orguser.forms.formField.typeBudgetInputOptions.calendar',
    },
];

export const TYPE_BUDGET_TRANSPARENCY_SHOW = 0;
export const TYPE_BUDGET_TRANSPARENCY_SHOW_EXISTS = 1;

export const budgetTransparency: ResourceFieldBudgetOptionTypes[] = [
    {
        value: TYPE_BUDGET_TRANSPARENCY_SHOW,
        label: 'orguser.forms.formField.typeBudgetTransparencyOptions.show',
    },
    {
        value: TYPE_BUDGET_TRANSPARENCY_SHOW_EXISTS,
        label: 'orguser.forms.formField.typeBudgetTransparencyOptions.showIfExists',
    },
];

export const TAB_FIELD_DATA = 1;
export const TAB_GDPR_SETTINGS = 2;
export const TAB_INTERNAL = 3;

export const tabs: TabItemType[] = [
    {
        title: 'orguser.forms.fieldFormTabs.fieldData',
        tabNumber: TAB_FIELD_DATA,
        isDisabled: false,
    },
    {
        title: 'orguser.forms.fieldFormTabs.gdpr',
        tabNumber: TAB_GDPR_SETTINGS,
        isDisabled: false,
    },
    {
        title: 'orguser.forms.fieldFormTabs.internal',
        tabNumber: TAB_INTERNAL,
        isDisabled: false,
    },
];

export const isMetadataVariableDisabled = (initialValues: FormFieldTypes, isPopup: boolean) => {
    const {isRequired, type, resourceFieldType} = initialValues;
    if (type === FORM_FIELD_RESOURCE_FIELD && resourceFieldType === RESOURCE_FIELD_TYPE_CALENDAR) {
        return false;
    }
    return (
        isPopup || !isRequired || [FORM_FIELD_UPLOADER, FORM_FIELD_RESOURCE_FIELD, FORM_FIELD_SIGNATURE].includes(type)
    );
};

export const getSelectedVariablesIds = (
    pages: FormPageTypes[],
    currentWorkflowVariables: WorkflowFieldVariableItemTypes[],
): number[] => {
    const variableIds: number[] = [];
    currentWorkflowVariables.forEach(variable => {
        variableIds.push(variable.id);
    });
    const results: number[] = [];
    pages.forEach(page => {
        const sections = page.sections;
        sections.forEach(section => {
            const fields = section.fields;
            if (fields && fields.length > 0) {
                fields.forEach(field => {
                    if (field.variable && variableIds.includes(field.variable)) {
                        results.push(field.variable);
                    }
                });
            }
        });
    });
    return results;
};

export interface FormVariablesOptionsListTypes {
    variable: string;
    title: string;
}

export interface FormVariablesListTypes {
    variable: string;
    title: string;
    isMultipleSelection?: boolean;
    options?: FormVariablesOptionsListTypes[];
}

export const getFormVariablesOptionsList = (fieldOptions: FieldOptionType[]): FormVariablesOptionsListTypes[] => {
    const results: FormVariablesOptionsListTypes[] = [];
    fieldOptions.forEach(option => {
        const {title, formVariable} = option;
        if (formVariable) {
            results.push({
                variable: formVariable,
                title,
            });
        }
    });
    return results;
};

export const getFormVariablesList = (
    pages: FormPageTypes[],
    pageId: number,
    sectionId: number,
    fieldId: number | null,
    isCreate?: boolean,
) => {
    const results: FormVariablesListTypes[] = [];
    const currentPagePriority = getCurrentPagePriority(pages, pageId);
    const currentSectionPriority = getCurrentSectionPriority(pages, pageId, sectionId);
    const currentFieldPriority = getCurrentFieldPriority(pages, pageId, sectionId, fieldId);
    pages.forEach(page => {
        if (page.priority <= currentPagePriority) {
            const {sections} = page;
            sections.forEach(section => {
                if (page.priority < currentPagePriority || section.priority <= currentSectionPriority) {
                    const {fields} = section;
                    fields.forEach(field => {
                        const {label, formVariable, type, fieldOptions, priority} = field;

                        const isAddToVariablesList =
                            formVariable &&
                            (page.priority < currentPagePriority ||
                                section.priority < currentSectionPriority ||
                                (section.priority === currentSectionPriority &&
                                    (priority <= currentFieldPriority || isCreate)));

                        if (isAddToVariablesList) {
                            if ((isImageOptionsList(type) || showOptionsList(type)) && fieldOptions) {
                                const options: FormVariablesOptionsListTypes[] =
                                    getFormVariablesOptionsList(fieldOptions);
                                results.push({
                                    variable: formVariable,
                                    title: label,
                                    isMultipleSelection: [FORM_FIELD_IMAGE_CHECK, FORM_FIELD_CHECKBOXES].includes(type),
                                    options,
                                });
                            } else {
                                results.push({
                                    variable: formVariable,
                                    title: label,
                                });
                            }
                        }
                    });
                }
            });
        }
    });
    return results;
};

export const getCustomButtonsForFieldTypeTemplateDescription = (
    pages: FormPageTypes[],
    pageId: number,
    sectionId: number,
    fieldId: number | null,
    isCreate?: boolean,
): MdxEditorCustomButtonType[] => {
    const results: MdxEditorCustomButtonType[] = [];
    const variables: MdxEditorCustomButtonType[] = [];

    const callbackPlaceholder = "{{ formField('%INTERNAL_NAME%') }}";
    const callbackConditionPlaceholder = "{% if %INTERNAL_NAME% == '%OPTION_NAME%' %} ---- {% endif %}";
    const callbackMultipleConditionPlaceholder =
        "{% if '%OPTION_NAME%' in selectedOptions('%INTERNAL_NAME%') %} ---- {% endif %}";

    const formVariablesList = getFormVariablesList(pages, pageId, sectionId, fieldId, isCreate);

    formVariablesList.forEach(item => {
        const {title, variable, isMultipleSelection, options} = item;
        if (options) {
            variables.push({
                title,
                markdown: callbackPlaceholder.replace('%INTERNAL_NAME%', item.variable),
                isSelector: true,
            });
            options.forEach(option => {
                const {title, variable} = option;
                if (isMultipleSelection) {
                    variables.push({
                        title,
                        parentTitle: item.title,
                        markdown: callbackMultipleConditionPlaceholder
                            .replace('%INTERNAL_NAME%', item.variable)
                            .replace('%OPTION_NAME%', variable),
                        isSubItem: true,
                    });
                } else {
                    variables.push({
                        title,
                        parentTitle: item.title,
                        markdown: callbackConditionPlaceholder
                            .replace('%INTERNAL_NAME%', item.variable)
                            .replace('%OPTION_NAME%', variable),
                        isSubItem: true,
                    });
                }
            });
        } else {
            variables.push({
                title,
                markdown: callbackPlaceholder.replace('%INTERNAL_NAME%', item.variable),
            });
        }
    });

    if (variables.length > 0) {
        results.push({
            title: 'orguser.requesterCase.formVariables',
            markdown: '',
            items: variables,
        });
    }

    return results;
};

export const getCurrentPagePriority = (pages: FormPageTypes[], pageId: number): number => {
    const currentPage = pages.find(page => page.id === pageId);
    return currentPage ? currentPage.priority : 0;
};

export const getCurrentSectionPriority = (pages: FormPageTypes[], pageId: number, sectionId: number): number => {
    const currentPage = pages.find(page => page.id === pageId);
    if (currentPage) {
        const {sections} = currentPage;
        const currentSection = sections.find(section => section.id === sectionId);
        return currentSection ? currentSection.priority : 0;
    }
    return 0;
};

export const getCurrentFieldPriority = (
    pages: FormPageTypes[],
    pageId: number,
    sectionId: number,
    fieldId: number | null,
): number => {
    const currentPage = pages.find(page => page.id === pageId);
    if (currentPage) {
        const {sections} = currentPage;
        const currentSection = sections.find(section => section.id === sectionId);
        if (currentSection) {
            const {fields} = currentSection;
            const currentField = fields.find(field => field.id === Number(fieldId));
            return currentField ? currentField.priority : 0;
        }
    }
    return 0;
};

export interface CurrentFieldTypes {
    page: FormPageTypes;
    section: FormSectionTypes;
    field: FormFieldTypes;
}

export const getCurrentFieldInformation = (pages: FormPageTypes[], fieldId: number): CurrentFieldTypes | null => {
    for (let i = 0, n = pages.length; i < n; i++) {
        const {sections} = pages[i];
        for (let j = 0, l = sections.length; j < l; j++) {
            const {fields} = sections[j];
            for (let u = 0, m = fields.length; u < m; u++) {
                const field = fields[u];
                if (field.id === fieldId) {
                    return {
                        page: pages[i],
                        section: sections[j],
                        field,
                    };
                }
            }
        }
    }
    return null;
};

export const getPagesWithHigherPriorities = (pages: FormPageTypes[], pageId: number): FormPageTypes[] => {
    const currentPagePriority = getCurrentPagePriority(pages, pageId);
    return pages.filter(page => page.priority >= currentPagePriority);
};

export const getSectionsWithHigherPriorities = (
    sections: FormSectionTypes[],
    sectionId: number,
): FormSectionTypes[] => {
    const currentSection = sections.find(section => section.id === sectionId);
    if (currentSection) {
        return sections.filter(section => section.priority >= currentSection.priority);
    }
    return [];
};

export const getInitialSectionsList = (pages: FormPageTypes[], fieldId?: number): FormSectionTypes[] => {
    if (!fieldId) return [];
    const currentFieldInformation = getCurrentFieldInformation(pages, fieldId);
    return currentFieldInformation ? currentFieldInformation.page.sections : [];
};
